import { useDocStore } from "../../state/docStore";
import {
  MAX_STRETCH_GOALS,
  TEMPLATE_FIELD_META,
  type TemplateQuestionField,
} from "../../lib/types";
import { Input } from "../common/Input";
import { Textarea } from "../common/Textarea";
import { Button } from "../common/Button";
import "./FieldsPanel.css";

function SaveIndicator() {
  const { saveStatus } = useDocStore();
  if (saveStatus === "saving")
    return <span className="fields__save">Saving…</span>;
  if (saveStatus === "saved")
    return <span className="fields__save">All changes saved</span>;
  if (saveStatus === "error")
    return (
      <span className="fields__save fields__save--error">
        Couldn't save — retrying on next edit
      </span>
    );
  return null;
}

function StretchGoals() {
  const { doc, setField } = useDocStore();
  const goals = doc.stretchGoals;
  const atMax = goals.length >= MAX_STRETCH_GOALS;
  const nearMax = goals.length === MAX_STRETCH_GOALS - 1;

  const update = (index: number, value: string) => {
    const next = goals.slice();
    next[index] = value;
    setField("stretchGoals", next);
  };
  const remove = (index: number) => {
    setField(
      "stretchGoals",
      goals.filter((_, i) => i !== index),
    );
  };
  const add = () => {
    if (atMax) return;
    setField("stretchGoals", [...goals, ""]);
  };

  return (
    <div className="field">
      <div className="field__label-row">
        <span className="field__label">Stretch goals (prioritised)</span>
        <span className="muted" style={{ fontSize: "var(--text-xs)" }}>
          {goals.length}/{MAX_STRETCH_GOALS}
        </span>
      </div>
      <div className="stretch">
        {goals.map((g, i) => (
          <div className="stretch__row" key={i}>
            <span className="stretch__num">{i + 1}</span>
            <div className="stretch__input">
              <Input
                value={g}
                placeholder={`Stretch goal #${i + 1} (optional)`}
                onChange={(e) => update(i, e.target.value)}
                aria-label={`Stretch goal ${i + 1}`}
              />
            </div>
            <button
              type="button"
              className="stretch__remove"
              onClick={() => remove(i)}
              aria-label={`Remove stretch goal ${i + 1}`}
              title="Remove"
            >
              ×
            </button>
          </div>
        ))}

        {atMax ? (
          <div className="stretch__warn">
            You're at the {MAX_STRETCH_GOALS}-goal cap. For a 1-month build, keep
            these tightly prioritised — drop or merge before adding more.
          </div>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="stretch__add"
            onClick={add}
          >
            + Add stretch goal
          </Button>
        )}
        {nearMax && !atMax && (
          <div className="field__hint">
            One slot left — make sure each goal is genuinely achievable in a month.
          </div>
        )}
      </div>
    </div>
  );
}

function TemplateField({ field }: { field: TemplateQuestionField }) {
  const { doc, setField } = useDocStore();
  const meta = TEMPLATE_FIELD_META.find((m) => m.key === field)!;
  return (
    <div className="field">
      <Textarea
        label={meta.label}
        hint={meta.hint}
        placeholder={meta.placeholder}
        autoGrow
        value={doc[field]}
        onChange={(e) => setField(field, e.target.value)}
      />
    </div>
  );
}

export function FieldsPanel() {
  const { doc, setField } = useDocStore();

  return (
    <div className="fields">
      <div className="fields__header">
        <div className="fields__title">Project details</div>
        <SaveIndicator />
      </div>

      <div className="fields__body">
        {/* Scoping block — the focusing layer this app adds. */}
        <div className="fields__section">
          <div className="fields__section-title">Scope</div>
          <div className="scope-card">
            <div className="field">
              <Input
                label="Your name *"
                placeholder="e.g. Jane Tan"
                value={doc.participantName}
                onChange={(e) => setField("participantName", e.target.value)}
              />
            </div>
            <div className="field">
              <Input
                label="Contact email *"
                type="email"
                placeholder="e.g. jane@company.com"
                value={doc.contactEmail}
                onChange={(e) => setField("contactEmail", e.target.value)}
              />
            </div>
            <div className="field">
              <Input
                label="Company name"
                placeholder="e.g. Acme Pte Ltd"
                value={doc.companyName}
                onChange={(e) => setField("companyName", e.target.value)}
              />
            </div>
            <div className="scope-card__accent">
              <Textarea
                label="Main goal (exactly one)"
                hint="The single primary problem to solve in the month. Required to submit."
                placeholder="In one sentence: the one thing this project must achieve…"
                autoGrow
                value={doc.mainGoal}
                onChange={(e) => setField("mainGoal", e.target.value)}
              />
            </div>
            <StretchGoals />
          </div>
        </div>

        {/* The 14 template questions, in order. */}
        <div className="fields__section">
          <div className="fields__section-title">
            Project planning questions
          </div>
          {TEMPLATE_FIELD_META.map((m) => (
            <TemplateField key={m.key} field={m.key} />
          ))}
        </div>
      </div>
    </div>
  );
}
